import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr, API } from "@/lib/api";
import { FilePdf, EnvelopeSimple, Eye, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Reports() {
  const { activeBiz } = useOutletContext();
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(null);
  const [emailForm, setEmailForm] = useState({ to_email: "", note: "" });

  const reload = async () => {
    if (!activeBiz) return;
    try {
      const { data } = await api.get(`/businesses/${activeBiz.id}/reports`);
      setReports(data);
    } catch {}
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeBiz]);

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/businesses/${activeBiz.id}/reports/generate`);
      toast.success("Report generated");
      reload();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const preview = () => {
    const url = `${API}/businesses/${activeBiz.id}/reports/preview`;
    window.open(url + (url.includes("?") ? "&" : "?") + "_t=" + Date.now(), "_blank");
  };

  const sendEmail = async (rid) => {
    if (!emailForm.to_email) return toast.error("Enter recipient email");
    try {
      const { data } = await api.post(`/businesses/${activeBiz.id}/reports/${rid}/email`, emailForm);
      toast.success(data.mocked ? `MOCKED: would email ${emailForm.to_email} (${data.size_bytes} bytes)` : `Sent to ${emailForm.to_email}`);
      setEmailing(null);
      setEmailForm({ to_email: "", note: "" });
      reload();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-5xl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-tiny mb-1">Reports</div>
          <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="reports-title">Monthly Report Card</h1>
          <p className="text-zinc-500 mt-1 max-w-2xl">Branded PDF reports with before/after deltas, attribution by source, and quality warnings. Email straight to clients.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary !py-2 !text-[11px]" onClick={preview} data-testid="report-preview"><Eye size={12} weight="bold" /> Preview now</button>
          <button className="btn-primary !py-2 !text-[11px]" onClick={generate} disabled={busy} data-testid="report-generate">
            {busy ? <span className="spinner"></span> : <><Sparkle size={12} weight="fill" /> Generate report</>}
          </button>
        </div>
      </header>

      <div className="bento p-5 border-l-4 border-[#002FA7] bg-zinc-50 text-sm">
        <strong>How clients see it:</strong> The PDF is branded with your agency's name + color (set in Agency Hub → Branding). Includes the Lead Capture Score, Quality Health Score, before/after deltas vs the previous month, top-3 keyword performance, attribution table, and any active quality warnings.
      </div>

      {reports.length === 0 ? (
        <div className="bento p-12 text-center">
          <FilePdf size={28} className="mx-auto text-zinc-400 mb-3" />
          <h3 className="font-display text-xl font-bold mb-2">No reports yet</h3>
          <p className="text-zinc-600 max-w-md mx-auto">Hit "Preview now" to see a fresh PDF, or "Generate report" to log one. Schedule monthly so clients get one on the 1st of every month.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="bento p-5" data-testid={`report-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-lg">{r.label}</div>
                  <div className="font-mono text-xs text-zinc-500 mt-1">Created {new Date(r.created_at).toLocaleString()}</div>
                  {r.emailed_to && r.emailed_to.length > 0 && (
                    <div className="font-mono text-xs text-zinc-500 mt-1">
                      Emailed to: {r.emailed_to.map((e, i) => <span key={i} className="mr-2">{e.email}{e.mocked ? " (mock)" : ""}</span>)}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button className="btn-secondary !py-2 !text-[11px]" onClick={preview} data-testid={`report-view-${r.id}`}><Eye size={12} weight="bold" /> View PDF</button>
                  <button className="btn-dark !py-2 !text-[11px]" onClick={() => setEmailing(emailing === r.id ? null : r.id)} data-testid={`report-email-toggle-${r.id}`}><EnvelopeSimple size={12} weight="bold" /> Email to client</button>
                </div>
              </div>
              {emailing === r.id && (
                <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid={`report-email-form-${r.id}`}>
                  <input className="input-field md:col-span-1" type="email" placeholder="client@example.com" required value={emailForm.to_email} onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} data-testid={`report-to-${r.id}`} />
                  <input className="input-field md:col-span-2" placeholder="Optional note" value={emailForm.note} onChange={(e) => setEmailForm({ ...emailForm, note: e.target.value })} data-testid={`report-note-${r.id}`} />
                  <button className="btn-primary md:col-span-3 justify-center !py-2 !text-[11px]" onClick={() => sendEmail(r.id)} data-testid={`report-send-${r.id}`}>Send report</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
